/**
 * This is a set of helper functions to build treatments (aka, A/B tests and/or feature flags)
 * for our applications.
 *
 * ALL treatments must be registered at Split.io before they can be used AND included in the
 * treatments enum in the `constants/treatments.ts` file.
 */

import { SplitFactory } from '@splitsoftware/splitio'
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio'

/**
 * Split's official localhost/offline mode. When NEXT_PUBLIC_SPLIT_IO_KEY is
 * the literal string 'localhost', the SDK never hits the network and serves
 * treatments from this map (NEXT_PUBLIC_SPLIT_LOCALHOST_FEATURES — a JSON
 * object of flag name → treatment). Flags absent from the map evaluate to
 * 'control'. Real environments use a real key, where `features` is ignored.
 */
const localhostFeatures = (): SplitIO.MockedFeaturesMap => {
  try {
    return JSON.parse(process.env.NEXT_PUBLIC_SPLIT_LOCALHOST_FEATURES || '{}')
  } catch {
    return {}
  }
}

const splitLocalhostConfig = (): Partial<SplitIO.IBrowserSettings> =>
  process.env.NEXT_PUBLIC_SPLIT_IO_KEY === 'localhost'
    ? { features: localhostFeatures() }
    : {}
import { TreatmentName } from '../constants/treatments'
import { TreatmentValue } from '../constants/treatmentValues'
import {
  ensureSplitRolloutPlan,
  getReleasedTreatment,
  getReleasedTreatmentConfig,
} from 'lib/splitRolloutPlan'
import { v4 as uuidv4 } from 'uuid'
import { setRudderstackAnonymousId } from './logIdentity'
import Cookies from 'js-cookie'

let isAnonymousClientReady = false
let isAnonymousClientTimedOut = false
let isUserClientReady = false
let isUserClientTimedOut = false

/**
 * Analytics consent must live in module scope (not React state):
 * `initializeSplitUserClient` builds a new factory on every user load, and
 * both initialize helpers are called from effects that may race Osano's
 * consent resolution. Idempotent `setSplitAnalyticsConsent` is the single
 * entry point that opens or tears down the factories.
 */
let analyticsConsentGranted = false
let pendingUserKey: string | null = null

let anonymousSplitFactory: SplitIO.IBrowserSDK
let userSplitClient: IBrowserClient
// The user factory is kept in module scope: the per-license-key shared clients
// are created off it and cached in `licenseKeyClients` (below, also module
// scope), so the factory and its cache always share one instance. This
// intentionally differs from `globalThis.userSplitClient` — within a single
// bundle both are co-initialized in `initializeSplitUserClient` and never
// diverge, and a client does not expose its factory, so it cannot be re-derived
// from `globalThis.userSplitClient`.
let userSplitFactory: SplitIO.IBrowserSDK

/**
 * Per-license-key "shared" Split clients, keyed on the license key.
 *
 * Some flags (e.g. SAFETY_PLAN, THRIVECARE_ACTIONS_DROPDOWN_SAFETY_PLAN) are
 * targeted in Harness by a User-traffic Standard segment whose MEMBERS are
 * license keys. "Is in segment" matches on the client KEY, not on passed
 * attributes, so those flags only resolve when the SDK client is keyed on the
 * license key itself. These shared clients are created lazily off the user
 * factory, cached by license key, and never destroyed (destroying any client
 * off a factory can tear down the whole factory).
 */
const licenseKeyClients = new Map<
  string,
  { client: IBrowserClient; isReady: boolean; isTimedOut: boolean }
>()

function destroySplitClients(): void {
  // Factory-level destroy also tears down per-license-key shared clients —
  // do not iterate licenseKeyClients and destroy each client (that can tear
  // down the whole factory mid-iteration).
  // IBrowserSDK typings in @splitsoftware/splitio@10.25.1 omit `destroy`
  // even though the runtime SDK exposes it on the factory.
  const anonymousFactoryWithDestroy = anonymousSplitFactory as
    | (SplitIO.IBrowserSDK & { destroy?: () => Promise<void> })
    | undefined
  const userFactoryWithDestroy = userSplitFactory as
    | (SplitIO.IBrowserSDK & { destroy?: () => Promise<void> })
    | undefined

  if (typeof anonymousFactoryWithDestroy?.destroy === 'function') {
    void anonymousFactoryWithDestroy.destroy().catch(() => {
      // Teardown is best-effort; a failed flush must not reject unhandled.
    })
  }

  if (typeof userFactoryWithDestroy?.destroy === 'function') {
    void userFactoryWithDestroy.destroy().catch(() => {
      // Teardown is best-effort; a failed flush must not reject unhandled.
    })
  }

  anonymousSplitFactory = undefined
  userSplitFactory = undefined
  userSplitClient = undefined
  licenseKeyClients.clear()
  isAnonymousClientReady = false
  isAnonymousClientTimedOut = false
  isUserClientReady = false
  isUserClientTimedOut = false
  delete globalThis.anonymousSplitClient
  delete globalThis.userSplitClient
  delete globalThis.anonymousId

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('anonymousId')
  }

  Cookies.remove('anonId')
}

function getFallbackTreatment<T extends TreatmentName>(
  name: T
): TreatmentValue<T> {
  return (getReleasedTreatment(name) ?? 'control') as TreatmentValue<T>
}

function getFallbackTreatmentConfig(name: TreatmentName): string {
  return getReleasedTreatmentConfig(name) ?? '{}'
}

/**
 * Called from UserContextWrapper via subscribeToOsanoAnalyticsConsent.
 * Denied callbacks always kick the splitChanges fetch — including the
 * initial onConsentChange(false), which would no-op if we only reacted
 * to a true→false transition (module default is already false).
 */
export function setSplitAnalyticsConsent(granted: boolean): void {
  if (granted) {
    if (!analyticsConsentGranted) {
      analyticsConsentGranted = true
      initializeSplitAnonymousClient()

      if (pendingUserKey) {
        initializeSplitUserClient(pendingUserKey)
      }
    }

    return
  }

  analyticsConsentGranted = false
  destroySplitClients()
  void ensureSplitRolloutPlan()
}

export function isSplitAnalyticsConsentGranted(): boolean {
  return analyticsConsentGranted
}

export function initializeSplitAnonymousClient(): void {
  if (!analyticsConsentGranted) {
    return
  }

  // Already initialized — avoid rebuilding the factory / rewriting storage.
  if (anonymousSplitFactory) {
    return
  }

  /**
   * First, we get-or-set a UUID for our anonymous sessions that we will store on the localStorage
   * and will keep handy in the globalThis namespace. We will also set the rudderstack anonymousId to
   * be the same value.
   */
  const anonymousId = localStorage.getItem('anonymousId') || uuidv4()
  globalThis.anonymousId = anonymousId
  localStorage.setItem('anonymousId', anonymousId)
  anonymousSplitFactory = SplitFactory({
    core: {
      authorizationKey:
        process.env.NEXT_PUBLIC_SPLIT_IO_KEY ||
        'j7ofd94c2124639n1jsc2dng9vo1hophc8vf',
      key: anonymousId, // This must be overwritten before we you use the client!!!
      trafficType: 'anonymous',
    },
    startup: {
      readyTimeout: 1.5, // seconds
    },
    ...splitLocalhostConfig(),
  })

  setRudderstackAnonymousId(anonymousId)
  Cookies.set('anonId', anonymousId)

  globalThis.anonymousSplitClient = anonymousSplitFactory.client()
  // A freshly created client is neither ready nor timed out yet.
  isAnonymousClientReady = false
  isAnonymousClientTimedOut = false

  globalThis.anonymousSplitClient.on(
    globalThis.anonymousSplitClient.Event.SDK_READY,
    () => {
      isAnonymousClientReady = true
    }
  )
  // Mirror the user / per-license-key clients: if the anonymous client hits its
  // readyTimeout without syncing (blocked CDN, ad-blocker, offline), mark it
  // terminal so readiness polls / route guards stop waiting instead of treating
  // CONTROL as "still resolving" forever. A later SDK_READY still flips
  // isAnonymousClientReady if the SDK recovers.
  globalThis.anonymousSplitClient.on(
    globalThis.anonymousSplitClient.Event.SDK_READY_TIMED_OUT,
    () => {
      isAnonymousClientTimedOut = true
    }
  )
}

export function initializeSplitUserClient(userkey: string): void {
  /**
   * This client will be used for the user traffic type.
   *
   */
  const lowerCaseUserKey = userkey.toLowerCase()
  // Always record so a user who logs in pre-consent still gets a user
  // client the moment consent arrives.
  pendingUserKey = lowerCaseUserKey

  if (!analyticsConsentGranted) {
    return
  }

  userSplitFactory = SplitFactory({
    core: {
      authorizationKey:
        process.env.NEXT_PUBLIC_SPLIT_IO_KEY ||
        'j7ofd94c2124639n1jsc2dng9vo1hophc8vf',
      key: lowerCaseUserKey, // This is the key that you can use to identify the user in the split dashboard
      trafficType: 'user',
    },
    startup: {
      readyTimeout: 1.5, // seconds
    },
    ...splitLocalhostConfig(),
  })

  // A freshly created user factory invalidates any per-license-key shared
  // clients built off a previous factory instance.
  licenseKeyClients.clear()

  userSplitClient = userSplitFactory.client()
  globalThis.userSplitClient = userSplitClient
  // A freshly created client is neither ready nor timed out yet — clear both
  // flags so isUserSplitReady()/isUserSplitSettled() don't report a prior
  // session's client as settled before this one's SDK events arrive.
  isUserClientReady = false
  isUserClientTimedOut = false

  globalThis.userSplitClient.on(
    globalThis.userSplitClient.Event.SDK_READY,
    () => {
      // eslint-disable-next-line
      isUserClientReady = true
    }
  )
  // Mirror the per-license-key clients: if the user client hits its readyTimeout
  // without syncing (blocked CDN, ad-blocker, offline), mark it terminal so
  // readiness polls / route guards stop waiting instead of treating CONTROL as
  // "still resolving" forever. A later SDK_READY still flips isUserClientReady
  // if the SDK recovers.
  globalThis.userSplitClient.on(
    globalThis.userSplitClient.Event.SDK_READY_TIMED_OUT,
    () => {
      // eslint-disable-next-line
      isUserClientTimedOut = true
    }
  )
}

/*
 T is the TreatmentName and is provided to TreatmentValue to auto-select
 the return type based on TreatmentName.
 Pass optional attributes parameter to handle treatments which need
 targeting with custom attributes
*/
export function getTreatment<T extends TreatmentName>(
  name: T,
  attributes?: SplitIO.Attributes
): TreatmentValue<T> {
  if (!isSplitReady()) {
    return getFallbackTreatment(name)
  }

  return globalThis.anonymousSplitClient.getTreatment(name, attributes)
}

export function getUserTreatment<T extends TreatmentName>(
  name: T,
  attributes?: SplitIO.Attributes
): TreatmentValue<T> {
  if (!isUserSplitReady()) {
    return getFallbackTreatment(name)
  }

  return globalThis.userSplitClient.getTreatment(name, attributes)
}

/**
 * Lazily create (and cache) a Split "shared client" keyed on a license key,
 * off the existing user factory. Used to evaluate flags against User-traffic
 * segments whose MEMBERS are license keys (see `licenseKeyClients` above).
 * Returns undefined until the user factory has been initialized.
 */
function getLicenseKeyClient(
  licenseKey: string | number
): IBrowserClient | undefined {
  if (!userSplitFactory) {
    return undefined
  }

  const key = String(licenseKey)
  const cached = licenseKeyClients.get(key)

  if (cached) {
    return cached.client
  }

  const client = userSplitFactory.client(key, 'user')
  const entry = { client, isReady: false, isTimedOut: false }
  licenseKeyClients.set(key, entry)
  client.on(client.Event.SDK_READY, () => {
    entry.isReady = true
  })
  // If the client hits its readyTimeout without syncing (e.g. a dropped
  // network), mark it terminal so callers stop waiting — otherwise `isReady`
  // stays false forever and any readiness poll runs indefinitely. A later
  // SDK_READY still flips `isReady`, so a fresh evaluation picks up the real
  // treatment if the SDK recovers.
  client.on(client.Event.SDK_READY_TIMED_OUT, () => {
    entry.isTimedOut = true
  })
  return client
}

export function isLicenseKeySplitReady(licenseKey: string | number): boolean {
  return !!licenseKeyClients.get(String(licenseKey))?.isReady
}

/**
 * "Settled" = the per-key client reached a TERMINAL state: either SDK_READY
 * (treatment is trustworthy) or SDK_READY_TIMED_OUT (it gave up syncing). Use
 * this to decide when to STOP waiting on the client (poll termination, route
 * guards); use `isLicenseKeySplitReady` when you need the treatment value to be
 * trustworthy. On a timeout the treatment stays 'control' until the SDK recovers.
 */
export function isLicenseKeySplitSettled(licenseKey: string | number): boolean {
  const entry = licenseKeyClients.get(String(licenseKey))
  return !!entry && (entry.isReady || entry.isTimedOut)
}

/**
 * True once the per-key client hit SDK_READY_TIMED_OUT. Timeout-ONLY signal (not
 * ready-inclusive): use it as the terminal escape for a value-based "resolving"
 * check without racing the SDK_READY value commit.
 */
export function isLicenseKeySplitTimedOut(
  licenseKey: string | number
): boolean {
  return !!licenseKeyClients.get(String(licenseKey))?.isTimedOut
}

/**
 * Evaluate a treatment keyed on a license key (see `getLicenseKeyClient`).
 * Lazily initializes the per-key client on first call; returns 'control' until
 * that key's client is SDK_READY. Mirrors `getUserTreatment`.
 */
export function getLicenseKeyTreatment<T extends TreatmentName>(
  name: T,
  licenseKey: string | number,
  attributes?: SplitIO.Attributes
): TreatmentValue<T> {
  const client = getLicenseKeyClient(licenseKey)

  if (!client || !isLicenseKeySplitReady(licenseKey)) {
    return getFallbackTreatment(name)
  }

  return client.getTreatment(name, attributes) as TreatmentValue<T>
}

export function getSmartTreatment<T extends TreatmentName>(
  name: T
): TreatmentValue<T> {
  // Try user client first if it's ready
  if (isUserSplitReady()) {
    return globalThis.userSplitClient.getTreatment(name)
  }

  // Fall back to anonymous client if user client is not ready
  if (isSplitReady()) {
    return globalThis.anonymousSplitClient.getTreatment(name)
  }

  return getFallbackTreatment(name)
}

export function isSplitReady(): boolean {
  return !!globalThis.anonymousSplitClient && isAnonymousClientReady
}

/**
 * True once the anonymous client hit SDK_READY_TIMED_OUT (gave up its initial
 * sync). Distinct from readiness: a timed-out client is NOT ready (its
 * treatments stay 'control'), but callers that only need to know when to STOP
 * waiting can treat this as terminal.
 */
export function isSplitTimedOut(): boolean {
  return isAnonymousClientTimedOut
}

/**
 * "Settled" = the anonymous client reached a TERMINAL state: SDK_READY (values
 * trustworthy) or SDK_READY_TIMED_OUT (gave up). Analog of
 * `isUserSplitSettled` / `isLicenseKeySplitSettled`; use it to decide when to
 * stop waiting, and `isSplitReady` when the value must be trustworthy.
 */
export function isSplitSettled(): boolean {
  return isSplitReady() || isAnonymousClientTimedOut
}

export function isUserSplitReady(): boolean {
  return !!globalThis.userSplitClient && isUserClientReady
}

/**
 * True once the user client hit SDK_READY_TIMED_OUT (gave up its initial sync).
 * Distinct from readiness: a timed-out client is NOT ready (its treatments stay
 * 'control'), but callers that only need to know when to STOP waiting can treat
 * this as terminal.
 */
export function isUserSplitTimedOut(): boolean {
  return isUserClientTimedOut
}

/**
 * "Settled" = the user client reached a TERMINAL state: SDK_READY (values
 * trustworthy) or SDK_READY_TIMED_OUT (gave up). Analog of
 * `isLicenseKeySplitSettled`; use it to decide when to stop waiting, and
 * `isUserSplitReady` when the value must be trustworthy.
 */
export function isUserSplitSettled(): boolean {
  return isUserSplitReady() || isUserClientTimedOut
}

export function getTreatmentWithConfig<T extends TreatmentName>(
  name: T
): { treatment: TreatmentValue<T>; config: string } {
  if (!isSplitReady()) {
    return {
      treatment: getFallbackTreatment(name),
      config: getFallbackTreatmentConfig(name),
    }
  }

  return globalThis.anonymousSplitClient.getTreatmentWithConfig(name)
}

export function getUserTreatmentWithConfig<T extends TreatmentName>(
  name: T
): { treatment: TreatmentValue<T>; config: string } {
  if (!isUserSplitReady()) {
    return {
      treatment: getFallbackTreatment(name),
      config: getFallbackTreatmentConfig(name),
    }
  }

  return globalThis.userSplitClient.getTreatmentWithConfig(name)
}

export { userSplitClient }
